import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import Button from "../base/Button";

type DropdownItem = {
  name: string;
  href: string;
  dividerBefore?: boolean;
};

type NavigationItem = {
  name: string;
  href: string;
  hasDropdown?: boolean;
  linkLabelToHref?: boolean;
  dropdownItems?: DropdownItem[];
};

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const navigation: NavigationItem[] = [
    { name: t("nav.home"), href: "/" },
    {
      name: t("nav.wms.title"),
      href: "/wms",
      hasDropdown: true,
      dropdownItems: [
        { name: t("nav.wms.ecommerce"), href: "/wms/ecommerce" },
        { name: t("nav.wms.thirdParty"), href: "/wms/3pl" },
        {
          name: t("nav.wms.hkGuide"),
          href: "/hong-kong-wms-system",
          dividerBefore: true,
        },
        { name: t("nav.tplsp"), href: "/tplsp" },
        { name: t("nav.wms.selectionGuide"), href: "/wms-tms-selection-guide" },
      ],
    },
    {
      name: t("nav.tms.title"),
      href: "/tms",
      hasDropdown: true,
      linkLabelToHref: true,
      dropdownItems: [
        { name: t("nav.tms.address"), href: "/tms/address-standardization" },
        { name: t("nav.tms.selectionGuide"), href: "/wms-tms-selection-guide" },
      ],
    },
    {
      name: t("nav.erp"),
      href: "/erp",
      hasDropdown: true,
      linkLabelToHref: true,
      dropdownItems: [
        { name: t("nav.erpCaseStudies"), href: "/erp/case-studies" },
      ],
    },
    {
      name: t("nav.knowledge"),
      href: "/knowledge",
      hasDropdown: true,
      linkLabelToHref: true,
      dropdownItems: [
        { name: t("nav.knowledgeArticles"), href: "/knowledge" },
        { name: t("nav.insights"), href: "/insights" },
      ],
    },
    { name: t("nav.about"), href: "/about" },
  ];

  const toggleLanguage = () => {
    const newLang = i18n.language === "zh" ? "en" : "zh";
    i18n.changeLanguage(newLang);
  };

  // Initialize language on component mount
  useEffect(() => {
    if (!i18n.language || i18n.language === "en") {
      i18n.changeLanguage("zh");
    }
  }, [i18n]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdown]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="https://static.readdy.ai/image/d8e2421d44e5bedaac00a22953471cdd/42b2cfa69c10d0d49829ad105a385c2b.png"
                alt="AUTOCHAIN TECHNOLOGY"
                className="h-10 object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((navItem) => (
              <div key={navItem.name} className="relative">
                {navItem.hasDropdown ? (
                  <div className="relative group">
                    <div className="flex items-center space-x-1 md:mr-[-8px]">
                      {navItem.linkLabelToHref ? (
                        <Link
                          to={navItem.href}
                          className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                            location.pathname === navItem.href
                              ? "text-blue-600"
                              : ""
                          }`}
                        >
                          {navItem.name}
                        </Link>
                      ) : (
                        <button className="text-gray-700 hover:text-blue-600 font-medium transition-colors cursor-pointer">
                          <span>{navItem.name}</span>
                        </button>
                      )}
                      <i className="ri-arrow-down-s-line text-sm text-gray-700 group-hover:text-blue-600 transition-colors"></i>
                    </div>
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="py-1">
                        {navItem.dropdownItems?.map((dropdownItem) => (
                          <div
                            key={dropdownItem.href}
                            className={
                              dropdownItem.dividerBefore
                                ? "mt-1 border-t border-gray-200 pt-1"
                                : ""
                            }
                          >
                            <Link
                              to={dropdownItem.href}
                              className="block px-4 py-2 text-md text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                            >
                              {dropdownItem.name}
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={navItem.href}
                    className={`text-gray-700 hover:text-blue-600 font-medium transition-colors ${
                      location.pathname === navItem.href ? "text-blue-600" : ""
                    }`}
                  >
                    {navItem.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Language Switcher & Contact Button */}
          <div className="hidden md:flex items-center space-x-4">
            {/* <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 border border-gray-300 rounded-lg hover:border-blue-600 transition-colors cursor-pointer"
            >
              <i className="ri-global-line text-lg"></i>
              <span>{t("nav.language")}</span>
            </button> */}
            <button
              onClick={() => {
                const message = encodeURIComponent(
                  t("nav.language") === "中"
                    ? "您好！我想了解 AUTOCHAIN 的智能物流解決方案，希望預約免費演示。請問可以安排時間嗎？"
                    : "Hello! I would like to learn more about AUTOCHAIN's smart logistics solutions and book a free demo. Could you arrange a time for us?"
                );
                window.open(
                  `https://wa.me/85294252903?text=${message}`,
                  "_blank"
                );
              }}
              className="font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center text-white hover:bg-opacity-90 focus:ring-4 focus:ring-opacity-30 px-6 py-3 text-base"
              style={{ backgroundColor: "rgb(67, 209, 199)" }}
            >
              {t("nav.contact")}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600 cursor-pointer"
            >
              <i
                className={`ri-${isMenuOpen ? "close" : "menu"}-line text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        {item.linkLabelToHref ? (
                          <Link
                            to={item.href}
                            className={`flex-1 text-gray-700 hover:text-blue-600 font-medium ${
                              location.pathname === item.href
                                ? "text-blue-600"
                                : ""
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(
                                openDropdown === item.name ? null : item.name
                              );
                            }}
                            className={`flex-1 text-left text-gray-700 hover:text-blue-600 font-medium cursor-pointer ${
                              location.pathname.startsWith(item.href)
                                ? "text-blue-600"
                                : ""
                            }`}
                          >
                            <span>{item.name}</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(
                              openDropdown === item.name ? null : item.name
                            );
                          }}
                          className={`text-gray-700 hover:text-blue-600 cursor-pointer ${
                            location.pathname.startsWith(item.href)
                              ? "text-blue-600"
                              : ""
                          }`}
                          aria-label={`Toggle ${item.name} submenu`}
                        >
                          <i
                            className={`ri-arrow-down-s-line transition-transform ${
                              openDropdown === item.name ? "rotate-180" : ""
                            }`}
                          ></i>
                        </button>
                      </div>
                      {openDropdown === item.name && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.dropdownItems?.map((dropdownItem) => (
                            <div
                              key={dropdownItem.href}
                              className={
                                dropdownItem.dividerBefore
                                  ? "mt-3 border-t border-gray-200 pt-3"
                                  : ""
                              }
                            >
                              <Link
                                to={dropdownItem.href}
                                className={`block text-sm text-gray-600 hover:text-blue-600 ${
                                  location.pathname === dropdownItem.href
                                    ? "text-blue-600"
                                    : ""
                                }`}
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setOpenDropdown(null);
                                }}
                              >
                                {dropdownItem.name}
                              </Link>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-gray-700 hover:text-blue-600 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 border border-gray-300 rounded-lg hover:border-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-global-line text-lg"></i>
                  <span>{t("nav.language")}</span>
                </button>
                <button
                  onClick={() => {
                    const message = encodeURIComponent(
                      t("nav.language") === "中"
                        ? "您好！我想了解 AUTOCHAIN 的智能物流解決方案，希望預約免費演示。請問可以安排時間嗎？"
                        : "Hello! I would like to learn more about AUTOCHAIN's smart logistics solutions and book a free demo. Could you arrange a time for us?"
                    );
                    window.open(
                      `https://wa.me/85294252903?text=${message}`,
                      "_blank"
                    );
                    setIsMenuOpen(false);
                  }}
                  className="font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center text-white hover:bg-opacity-90 focus:ring-4 focus:ring-opacity-30 px-6 py-3 text-base flex-1 ml-4"
                  style={{ backgroundColor: "rgb(67, 209, 199)" }}
                >
                  {t("nav.contact")}
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
